import authService, { TOKEN_TYPES } from "./authService";
import { toBackendTrialId } from "@/utils/trialIdMap";
import type {
  EnrollmentTargetRow,
  UpsertTargetPayload,
} from "@/views/TrialEnrollment/enrollmentTarget.types";

export class EnrollmentTargetApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "EnrollmentTargetApiError";
    this.status = status;
  }
}

const base = (trialId: string) =>
  `${import.meta.env.VITE_API_URL}/data/trial/${toBackendTrialId(trialId)}/enrollment_targets`;

/** Same shape as planOfRecordService's — DRF validation errors come back as
 * {field: ["message", ...]}, not {detail: "..."}. */
function extractErrorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (typeof record.detail === "string") return record.detail;
    const fieldErrors = Object.values(record)
      .flat()
      .filter((value): value is string => typeof value === "string");
    if (fieldErrors.length > 0) return fieldErrors.join(" ");
  }
  return `Request failed (${status})`;
}

export const enrollmentTargetService = {
  upsertTarget: async (
    trialId: string,
    payload: UpsertTargetPayload,
  ): Promise<EnrollmentTargetRow> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
    const response = await fetch(`${base(trialId)}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const responsePayload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new EnrollmentTargetApiError(
        extractErrorMessage(responsePayload, response.status),
        response.status,
      );
    }
    return responsePayload as EnrollmentTargetRow;
  },
};
